import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getToken } from "@/lib/auth";

/**
 * Route-level auth gate: unauthenticated visitors are redirected before the
 * page component mounts at all.
 *
 * AppShell also checks the token, but it can only gate what it *renders* --
 * and every page calls AppShell from inside its own JSX, so the page's data
 * effects have already fired by then. Visiting a protected URL while signed
 * out therefore issued a real API call, took a 401, and could flash an error
 * banner ("Couldn't reach the backend...") on the way to the login screen.
 * Guarding at the route means the fetch never happens.
 *
 * `replace` so an unauthenticated visit leaves no history entry to go "back"
 * to, and the attempted path is preserved in state so a future change could
 * return the user there after signing in.
 */
export default function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation();

  if (!getToken()) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}
